import { Button } from '@HoudiniUI/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';
import { Industry, IOperationResultNoValue } from '@ViewModels';
import { AutomationArchiveConfirmModal } from '@WebComponents/automation/AutomationArchiveConfirmModal';
import { css, StyleDeclarationValue } from 'aphrodite';
import { stringify as toQueryString } from 'query-string';
import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { ILocationState } from '../../../../models';
import { ISystemAutomationTemplate } from '../../../../models/AdminModels';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import {
	useArchiveIndustryAutomationTemplateMutation,
	usePublishIndustryAutomationTemplateMutation,
} from '../../../../queries/Admin/AutomationTemplate';
import {
	IndustryAutomationTemplateData,
	useIndustryAutomationTemplatesData,
} from '../../../../queries/Admin/AutomationTemplate/useIndustryAutomationTemplatesData';
import { DuplicateSystemAutomationTemplateModal } from '../../../components/templates/DuplicateSystemAutomationTemplateModal';
import { TemplatesByIndustry } from '../../../components/templates/TemplatesByIndustry';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	industry: Industry;
	styles?: StyleDeclarationValue[];
}

export const AutomationTemplatesByIndustry: React.FC<IProps> = props => {
	const { className, styles, industry } = props;
	const { logInput, logApiError, logPageView } = useEventLogging('AutomationTemplatesByIndustry');
	const history = useHistory();
	const errorMessages = useErrorMessages();
	const { postNotification } = useLocalNotificationService<ISystemAutomationTemplate>();

	const [templateToDuplicate, setTemplateToDuplicate] = React.useState<IndustryAutomationTemplateData | undefined>();
	const [templateToPublish, setTemplateToPublish] = React.useState<IndustryAutomationTemplateData | undefined>();
	const [templateToArchive, setTemplateToArchive] = React.useState<IndustryAutomationTemplateData | undefined>();
	const [templateToDelete, setTemplateToDelete] = React.useState<IndustryAutomationTemplateData | undefined>();

	const templatesData = useIndustryAutomationTemplatesData({ industry });
	const publishMutation = usePublishIndustryAutomationTemplateMutation();
	const archiveMutation = useArchiveIndustryAutomationTemplateMutation();

	React.useEffect(() => {
		logPageView(`${industry.split(' ').join()}AutomationTemplates`);
	}, [logPageView, industry]);

	const onDuplicateTemplateModalRequestClose = () => {
		setTemplateToDuplicate(null);
	};

	const onTemplateClicked = React.useCallback(
		(template: IndustryAutomationTemplateData) => {
			logInput('Edit', 'Click', { id: template.id, industry });
			const locationState: ILocationState<IndustryAutomationTemplateData, any> = { viewModel: template as any };
			history.push({
				pathname: `/automations/${template.id}/canvas`,
				search: toQueryString({ industry }),
				state: locationState,
			});
		},
		[history, logInput, industry]
	);

	const onMenuItemClicked = React.useCallback(
		(template: IndustryAutomationTemplateData, menuItem: 'edit' | 'archive' | 'delete' | 'publish' | 'copy') => {
			switch (menuItem) {
				case 'copy': {
					logInput('CopyAction', 'Click', { id: template.id, industry });
					setTemplateToDuplicate(template);
					break;
				}
				case 'edit': {
					logInput('EditAction', 'Click', { id: template.id, industry });
					const locationState: ILocationState<IndustryAutomationTemplateData, any> = { viewModel: template as any };
					history.push({
						pathname: `/automations/${template.id}/canvas`,
						search: toQueryString({ industry }),
						state: locationState,
					});
					break;
				}
				case 'publish': {
					logInput('PublishAction', 'Click', { id: template.id, industry });
					setTemplateToPublish(template);
					break;
				}
				case 'delete': {
					logInput('DeleteAction', 'Click', { id: template.id, industry });
					setTemplateToDelete(template);
					break;
				}
				case 'archive': {
					logInput('ArchiveAction', 'Click', { id: template.id, industry });
					setTemplateToArchive(template);
					break;
				}
				default: {
					break;
				}
			}
		},
		[logInput, industry, history]
	);

	const onPublishConfirmed = () => {
		if (!templateToPublish) {
			return;
		}
		const template = templateToPublish;
		publishMutation.mutate(
			{
				templateId: template.id,
				industry,
				automationTemplate: template.template,
			},
			{
				onSuccess: publishedTemplateModel => {
					postNotification({
						info: publishedTemplateModel as any,
						topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
					});
					setTemplateToPublish(undefined);
					toast.success(`"${template.name || 'Automation'}" published.`);
				},
				onError: (error: IOperationResultNoValue) => {
					logApiError('PublishAction-Error', error);
					errorMessages.pushApiError(error);
				},
			}
		);
	};

	const onArchiveConfirmed = () => {
		if (!templateToArchive) {
			return;
		}
		const template = templateToArchive;
		archiveMutation.mutate(
			{
				templateId: template.id,
				industry,
				automationTemplate: template.template,
			},
			{
				onSuccess: () => {
					setTemplateToArchive(undefined);
					toast.success(`"${template.name || 'Automation'}" archived.`);
				},
				onError: (error: IOperationResultNoValue) => {
					logApiError('ArchiveAction-Error', error);
					errorMessages.pushApiError(error);
				},
			}
		);
	};

	const onDeleteConfirmed = () => {
		if (!templateToDelete) {
			return;
		}
		const template = templateToDelete;
		archiveMutation.mutate(
			{
				templateId: template.id,
				industry,
				automationTemplate: template.template,
			},
			{
				onSuccess: () => {
					setTemplateToDelete(undefined);
					toast.success(`"${template.name || 'Automation'}" deleted.`);
				},
				onError: (error: IOperationResultNoValue) => {
					logApiError('DeleteAction-Error', error);
					errorMessages.pushApiError(error);
				},
			}
		);
	};

	const onPublishDialogOpenChange = (open: boolean) => {
		if (!open && !publishMutation.isPending) {
			setTemplateToPublish(undefined);
		}
	};

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} automation-templates-by-industry ${className || ''}`}
		>
			<TemplatesByIndustry
				industry={industry}
				name='Automations'
				onMenuItemClicked={onMenuItemClicked as any}
				onTemplateClicked={onTemplateClicked as any}
				templates={templatesData as any}
			/>
			<DuplicateSystemAutomationTemplateModal
				industryAutomationTemplate={templateToDuplicate}
				isOpen={!!templateToDuplicate}
				onRequestClose={onDuplicateTemplateModalRequestClose}
			/>
			<AutomationArchiveConfirmModal
				automationName={templateToArchive?.name}
				isArchive={true}
				isOpen={!!templateToArchive}
				isProcessing={archiveMutation.isPending && !!templateToArchive}
				onConfirm={onArchiveConfirmed}
				onRequestClose={() => {
					if (!archiveMutation.isPending) {
						setTemplateToArchive(undefined);
					}
				}}
			/>
			<AutomationArchiveConfirmModal
				automationName={templateToDelete?.name}
				isArchive={false}
				isOpen={!!templateToDelete}
				isProcessing={archiveMutation.isPending && !!templateToDelete}
				onConfirm={onDeleteConfirmed}
				onRequestClose={() => {
					if (!archiveMutation.isPending) {
						setTemplateToDelete(undefined);
					}
				}}
			/>
			<Dialog open={!!templateToPublish} onOpenChange={onPublishDialogOpenChange}>
				<DialogPopup styles={[styleSheet.publishPopup]} showCloseButton={!publishMutation.isPending}>
					<DialogHeader>
						<DialogTitle>Publish automation?</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<p className={css(styleSheet.publishDescription)}>
							{templateToPublish?.name ? (
								<>
									Publishing <strong>{templateToPublish.name}</strong> will make it available as a sample automation for
									all customers in this industry.
								</>
							) : (
								'Publishing will make this automation available as a sample for all customers in this industry.'
							)}
						</p>
					</DialogBody>
					<DialogFooter>
						<Button
							variant='secondary'
							onClick={() => setTemplateToPublish(undefined)}
							disabled={publishMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							variant='primary'
							onClick={onPublishConfirmed}
							disabled={publishMutation.isPending}
							isLoading={publishMutation.isPending}
						>
							Yes, publish automation
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};
